"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Record = void 0;
const core_1 = require("@apitable/core");
const core_2 = require("../core");
const selector_1 = require("../store/selector");
const private_1 = require("../utils/private");
const field_1 = require("./field");
/**
 * Datasheet row operations and information.
 *
 * Get the rows record, you can use {@link useRecord} (querying single record data), {@link useRecords} (batch query of record data)
 */
class Record {
    /**
     * @hidden
     */
    constructor(datasheetId, wCtx, recordId) {
        this.datasheetId = datasheetId;
        this.wCtx = wCtx;
        this.recordId = recordId;
    }
    getRecordData() {
        const state = this.wCtx.widgetStore.getState();
        const datasheetId = this.datasheetId;
        const record = core_2.Selectors.getRecord(state, this.recordId, datasheetId);
        return record;
    }
    getFieldRole(fieldId) {
        const state = this.wCtx.widgetStore.getState();
        const fieldPermissionMap = (0, selector_1.getFieldPermissionMap)(state, this.datasheetId);
        return (0, selector_1.getFieldRoleByFieldId)(fieldPermissionMap, fieldId);
    }
    /**
     * Gets the cell value of the given field for this record.
     *
     * @param fieldId The field ID whose cell value you'd like to get.
     * @returns
     *
     * #### Example
     * ```js
     * const cellValue = myRecord.getCellValue(mySingleLineTextFieldId);
     * console.log(cellValue);
     * // => 'cell value'
     * ```
     */
    getCellValue(fieldId) {
        const state = this.wCtx.widgetStore.getState();
        const field = core_2.Selectors.getField(state, fieldId, this.datasheetId);
        const cellValue = this._getCellValue(fieldId);
        return core_2.Field.bindContext(field, state).cellValueToOpenValue(cellValue);
    }
    /**
     * @hidden
     */
    _getCellValue(fieldId) {
        const state = this.wCtx.widgetStore.getState();
        const globalState = state;
        const snapshot = (0, selector_1.getSnapshot)(state, this.datasheetId);
        const field = core_2.Selectors.getField(globalState, fieldId, this.datasheetId);
        // wecom blocked fields
        if (!(0, field_1.showField)((0, core_1.getFieldTypeString)(field.type))) {
            return null;
        }
        // getCellValue determines the column permission. Return null if no permission
        if (this.getFieldRole(fieldId) === core_2.ConfigConstant.Role.None) {
            return null;
        }
        // remove cell value cache.
        (0, private_1.isSandbox)() && core_1.CacheManager.removeCellCacheByRecord(this.datasheetId, this.recordId);
        return core_2.Selectors.getCellValue(globalState, snapshot, this.recordId, fieldId);
    }
    /**
     * Gets the cell value of the given field of record, and convert to string type.
     *
     * @returns
     *
     * #### Example
     * ```js
     * const stringValue = myRecord.getCellValueString(myNumberFieldId);
     * console.log(stringValue);
     * // => '42'
     * ```
     */
    getCellValueString(fieldId) {
        const state = this.wCtx.widgetStore.getState();
        const datasheetId = this.datasheetId;
        const field = core_2.Selectors.getField(state, fieldId, this.datasheetId);
        const cellValue = this._getCellValue(fieldId);
        return core_2.Field.bindContext(field, state).cellValueToString(cellValue, { datasheetId });
    }
    /**
     *
     * The URL address of the record, which you can access in your browser,
     * opens the Wiggle Table interface and locates the record
     *
     * @param viewId
     * @returns
     *
     */
    url(viewId) {
        const state = this.wCtx.widgetStore.getState();
        const proto = window.location.protocol;
        const host = window.location.host;
        viewId = viewId ? viewId : core_2.Selectors.getActiveViewId(state, this.datasheetId);
        return `${proto}//${host}/workbench/${this.datasheetId}/${viewId}/${this.recordId}`;
    }
    /**
     * The ID for this record.
     */
    get id() {
        return this.recordId;
    }
    /**
     * The primary cell value in this record, formatted as a string.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myRecord.title);
     * // => '42'
     * ```
     */
    get title() {
        const state = this.wCtx.widgetStore.getState();
        const viewId = (0, selector_1.getActiveViewId)(state);
        const view = (0, selector_1.getView)(state, viewId, this.datasheetId);
        const primaryFieldId = view.columns[0].fieldId;
        return this.getCellValue(primaryFieldId) || (0, core_2.t)(core_2.Strings.record_unnamed);
    }
    /**
     * The number of comments on this record.
     * @returns
     *
     */
    get commentCount() {
        const record = this.getRecordData();
        return record.commentCount;
    }
}
exports.Record = Record;
